"use client"

import { Clock, Users, MapPin } from "lucide-react"
import { cn } from "@/lib/utils"

interface AgendaItem {
  startTime: string
  endTime: string
  title: string
  description?: string
  speakers?: string[]
  location?: string
  type: "break" | "session" | "panel" | "activity" | "debate" | "talk" | "competition" | "cafe"
  highlights?: string[]
}

const getTypeColor = (
  type: AgendaItem["type"],
): { bg: string; border: string; badge: string } => {
  const colors: Record<
    AgendaItem["type"],
    { bg: string; border: string; badge: string }
  > = {
    break: {
      bg: "bg-gray-50 dark:bg-gray-900",
      border: "border-gray-200 dark:border-gray-800",
      badge: "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300",
    },
    session: {
      bg: "bg-blue-50 dark:bg-blue-950",
      border: "border-blue-200 dark:border-blue-800",
      badge: "bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300",
    },
    panel: {
      bg: "bg-purple-50 dark:bg-purple-950",
      border: "border-purple-200 dark:border-purple-800",
      badge: "bg-purple-100 dark:bg-purple-900 text-purple-700 dark:text-purple-300",
    },
    activity: {
      bg: "bg-green-50 dark:bg-green-950",
      border: "border-green-200 dark:border-green-800",
      badge: "bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300",
    },
    debate: {
      bg: "bg-amber-50 dark:bg-amber-950",
      border: "border-amber-200 dark:border-amber-800",
      badge: "bg-amber-100 dark:bg-amber-900 text-amber-700 dark:text-amber-300",
    },
    talk: {
      bg: "bg-pink-50 dark:bg-pink-950",
      border: "border-pink-200 dark:border-pink-800",
      badge: "bg-pink-100 dark:bg-pink-900 text-pink-700 dark:text-pink-300",
    },
    competition: {
      bg: "bg-orange-50 dark:bg-orange-950",
      border: "border-orange-200 dark:border-orange-800",
      badge: "bg-orange-100 dark:bg-orange-900 text-orange-700 dark:text-orange-300",
    },
    cafe: {
      bg: "bg-yellow-50 dark:bg-yellow-950",
      border: "border-yellow-200 dark:border-yellow-800",
      badge: "bg-yellow-100 dark:bg-yellow-900 text-yellow-700 dark:text-yellow-300",
    },
  }
  return colors[type]
}

const agendaItems: AgendaItem[] = [
  {
    startTime: "10:00",
    endTime: "12:00",
    title: "Opportunity Corner: Stands and Employer Pavilion",
    type: "activity",
    description:
      "Interactive networking zone with employers, universities, and industry partners showcasing opportunities.",
  },
  {
    startTime: "12:00",
    endTime: "12:10",
    title: "Welcome Address",
    type: "session",
    speakers: ["UNILAG (VC or Prof. Olayemi)"],
    description: "Official opening remarks and summit introduction.",
  },
  {
    startTime: "12:10",
    endTime: "12:30",
    title: "Keynote: Launch of 'State of TNE in Nigeria' Report",
    type: "talk",
    speakers: [
      "Minister of Education OR Commissioner of Education, Enugu State",
    ],
    description:
      "Unveiling the transformative insights report on Transnational Education in Nigeria.",
  },
  {
    startTime: "12:30",
    endTime: "12:45",
    title: "Opening Plenary",
    type: "session",
    description: "Overview of the day's agenda and summit objectives.",
  },
  {
    startTime: "12:45",
    endTime: "13:15",
    title: "Panel: Future of Work (Expert Panel)",
    type: "panel",
    speakers: [
      "Adetomi Soyinka, Mastercard Foundation",
      "Olapeja Ibekwe, Sterling One Foundation",
      "Ashley Immanuel, Semicolon; Amabelle, Leap Africa",
    ],
    description:
      "Industry leaders discuss emerging career trends and workforce preparation.",
    highlights: [
        "Evolving job market dynamics",
        "Skills for future employment",
        "Career development pathways",
      ],
  },
  {
    startTime: "13:15",
    endTime: "13:45",
    title: "Panel: Future of Work (Future Leaders Panel)",
    type: "panel",
    speakers: [
      "Speaker, Commonwealth Youth Council",
      "Speaker, All-Africa Students Union",
      "Speaker, Evolv",
    ],
    description: "Young leaders share perspectives on career aspirations.",
    highlights: [
        "Youth perspectives on future careers",
        "Entrepreneurship opportunities",
        "Leadership development",
      ],
  },
  {
    startTime: "13:45",
    endTime: "14:15",
    title: "Panel: Communiversity (Access, Capacity & Financing)",
    type: "panel",
    speakers: [
      "Francis Omorojie, EduKate Africa (Moderator)",
      "Adekoyede Adewale, OPay",
      "Iveren, Ashoka",
    ],
    description: "Exploring accessible, quality education and financing solutions.",
    highlights: [
        "Education access solutions",
        "Capacity building initiatives",
        "Innovative financing models",
      ],
  },
  {
    startTime: "14:15",
    endTime: "14:45",
    title: "Affication Competition/Unilag Enactus",
    type: "competition",
    speakers: [
      "Prof Olayemi, UNILAG",
      "Francis Omorojie, Emvoyale Ventures",
      "Oliwatobi Oseni, PayShiga",
      "Dr Olusegun Omisakan, NESG Commissioner of Education, Enugu State",
    ],
    description:
      "University innovation competition showcasing entrepreneurial solutions.",
  },
  {
    startTime: "14:45",
    endTime: "15:15",
    title: "Break",
    type: "break",
    description: "Networking and refreshment break.",
  },
  {
    startTime: "15:15",
    endTime: "15:45",
    title: "Panel: AI and EdTech",
    type: "panel",
    speakers: ["Alex Onyia, CEO Educare; Nduka Ndubisi, Motherland; TBC"],
    description:
      "Exploring artificial intelligence and technology innovations in education.",
    highlights: [
        "AI applications in learning",
        "EdTech innovations",
        "Digital transformation in education",
      ],
  },
  {
    startTime: "15:45",
    endTime: "16:15",
    title: "Pitch my Dissertation Challenge/Centre for Entrepreneurial Studies",
    type: "competition",
    speakers: [
      "Dr Yewande, UNILAG",
      "Prof Olayemi, UNILAG",
      "Tosin Adebisi, LSE",
      "Dr Olusegun Omisakan, NESG",
      "Commissioner of Education, Enugu State",
    ],
    description:
      "Postgraduate researchers pitch commercialization-ready innovations.",
    highlights: [
        "Research innovation showcase",
        "Commercialization opportunities",
        "Investment potential evaluation",
      ],
  },
  {
    startTime: "16:15",
    endTime: "17:00",
    title: "Panel: Internationalisation",
    type: "panel",
    speakers: [
      "Moderators: Olusehepa Ogmbode, TAEG & Mr Keshimo, UNILAG",
      "Luke Wales, UoB",
      "Daniel Emannhor, British Council",
      "Deby Okoh, Brunel University (TBC)",
    ],
    description:
      "Discussion on global partnerships and transnational education opportunities.",
    highlights: [
        "International partnerships",
        "Student mobility programs",
        "Global education standards",
      ],
  },
  {
    startTime: "17:00",
    endTime: "17:45",
    title: "Inter-University Africa Debate: Team UNILAG vs. Team University of Ghana",
    type: "debate",
    speakers: [
      "Prof. Nick Vaughan-Williams, Provost UoB",
      "Dr Pascal, John Kufuor Foundation",
      "Emma Tarrant Tayou, Graduate Guidance Group",
      "Femi Bamigbetan, UBA/Olapeja Ibekwe/Ojoma Ochui (TBC)",
    ],
    description:
      "Competitive academic debate on Africa's future: innovation, economy, and policy.",
    highlights: [
        "Innovation and technology",
        "Economic development",
        "Policy implications",
      ],
  },
  {
    startTime: "17:45",
    endTime: "18:15",
    title: "Disrupted Talks",
    type: "talk",
    speakers: ["Tosin Adebisi, EduKate Africa"],
    description: "Featured talks by thought leaders and innovators.",
  },
  {
    startTime: "18:15",
    endTime: "18:25",
    title: "Vote of Thanks",
    type: "session",
    speakers: ["Mr Keshimo, UNILAG"],
    description: "Closing remarks and appreciation.",
  },
  {
    startTime: "18:25",
    endTime: "18:30",
    title: "Closing Remarks",
    type: "session",
    speakers: ["TBC"],
    description: "Final remarks from organizers.",
  },
  {
    startTime: "18:30",
    endTime: "19:00",
    title: "Presentation of Prizes & Awards",
    type: "activity",
    description: "Recognition and awards ceremony.",
  },
  {
    startTime: "19:00",
    endTime: "20:30",
    title: "Ubuntu Café: Creative Industries Panel",
    type: "cafe",
    speakers: [
      "Moderator: Femi Bamigbetan, Creative Director of REDTV",
      "In conversation with Nollywood Monalisa Chinda (TBC)",
    ],
    description:
      "Evening networking event with creative industry leaders and entertainment.",
    highlights: [
        "Creative industries insights",
        "Entertainment and culture",
        "Informal networking",
      ],
  },
]

export function AgendaSection() {
  const typeMapping: Record<string, AgendaItem["type"]> = {
    "10:00-12:00": "activity",
    "12:00-12:10": "session",
    "12:10-12:30": "talk",
    "12:30-12:45": "session",
    "12:45-13:15": "panel",
    "13:15-13:45": "panel",
    "13:45-14:15": "panel",
    "14:15-14:45": "competition",
    "14:45-15:15": "break",
    "15:15-15:45": "panel",
    "15:45-16:15": "competition",
    "16:15-17:00": "panel",
    "17:00-17:45": "debate",
    "17:45-18:15": "talk",
    "18:15-18:25": "session",
    "18:25-18:30": "session",
    "18:30-19:00": "activity",
    "19:00-20:30": "cafe",
  }

  return (
    <section id="agenda" className="py-20 md:py-28 bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      <div className="container mx-auto px-4">
        <div className="mx-auto mb-16 max-w-3xl text-center">
          <h2 className="mb-4 text-3xl font-bold text-foreground md:text-4xl lg:text-5xl text-balance">
            Conference Agenda
          </h2>
          <p className="text-lg text-muted-foreground text-pretty">
            DisruptED Summit 2026 - Wednesday, January 29, 2026
          </p>
          <p className="text-lg text-muted-foreground text-pretty mt-2">
            University of Lagos, Nigeria
          </p>
        </div>

        <div className="mx-auto max-w-4xl">
          <div className="space-y-3">
            {agendaItems.map((item, index) => {
              const colors = getTypeColor(item.type)
              return (
                <div
                  key={index}
                  className={cn(
                    "rounded-lg border p-4 transition-all hover:shadow-md",
                    colors.bg,
                    colors.border,
                  )}
                >
                  <div className="flex gap-4">
                    {/* Time Column */}
                    <div className="flex-shrink-0 min-w-[100px]">
                      <div className="flex items-center gap-2 text-sm font-semibold text-foreground">
                        <Clock className="h-4 w-4" />
                        <span>
                          {item.startTime} - {item.endTime}
                        </span>
                      </div>
                      <span
                        className={cn(
                          "inline-block mt-2 px-2 py-1 rounded text-xs font-medium capitalize",
                          colors.badge,
                        )}
                      >
                        {item.type}
                      </span>
                    </div>

                    {/* Content Column */}
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-foreground mb-1">
                        {item.title}
                      </h3>

                      {item.description && (
                        <p className="text-sm text-muted-foreground mb-2">
                          {item.description}
                        </p>
                      )}

                      {item.speakers && item.speakers.length > 0 && (
                        <div className="mt-2 pt-2 border-t border-current/10">
                          <div className="flex items-start gap-2 text-sm">
                            <Users className="h-4 w-4 flex-shrink-0 mt-0.5" />
                            <div>
                              {item.speakers.map((speaker, i) => (
                                <div key={i} className="text-muted-foreground">
                                  {speaker}
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      )}

                      {item.highlights && item.highlights.length > 0 && (
                        <div className="mt-3 pt-2 border-t border-current/10">
                          <p className="text-xs font-semibold text-foreground mb-2">
                            Key Focus Areas:
                          </p>
                          <ul className="space-y-1">
                            {item.highlights.map((highlight, i) => (
                              <li
                                key={i}
                                className="flex items-start gap-2 text-xs text-muted-foreground"
                              >
                                <span className="mt-1 h-1 w-1 flex-shrink-0 rounded-full bg-foreground/40" />
                                {highlight}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Legend */}
          <div className="mt-12 p-6 rounded-lg bg-card border border-border">
            <h3 className="text-sm font-semibold text-foreground mb-4">Event Types</h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {(
                [
                  "session",
                  "panel",
                  "talk",
                  "debate",
                  "competition",
                  "activity",
                  "break",
                  "cafe",
                ] as const
              ).map((type) => {
                const colors = getTypeColor(type)
                return (
                  <div key={type} className="flex items-center gap-2 text-sm">
                    <div
                      className={cn(
                        "h-3 w-3 rounded",
                        colors.badge.split(" ")[0],
                      )}
                    />
                    <span className="text-muted-foreground capitalize">{type}</span>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
